import { useEffect, useState } from 'react'
import { formatIN, useCountUp, useReducedMotion } from '../kit'
import { useTooltip } from './Tooltip'

// Where every questionnaire is: one bar per stage on a 26px track, square at
// the start and round at the end, in one hue that steps lighter down the
// stages because the stages are ordered. Each bar grows in turn and opens the
// list of the questionnaires it counts. A new answer moves each bar to its new
// width and counts its number to the new value.

export type Step = { key: string; label: string; count: number; href?: string }

const SHADES = [1, 0.82, 0.66, 0.5]

export function Pipeline({ steps, title, color = 'var(--hc-s1)' }: { steps: Step[]; title: string; color?: string }) {
  const reduced = useReducedMotion()
  const tip = useTooltip()
  const [grown, setGrown] = useState<boolean[]>(() => steps.map(() => reduced))
  const max = Math.max(1, ...steps.map((s) => s.count))

  useEffect(() => {
    if (reduced) {
      setGrown(steps.map(() => true))
      return
    }
    // The proposal's timing: the first bar after 80ms, each next one 120ms on.
    const timers = steps.map((_, i) => window.setTimeout(() => setGrown((g) => g.map((v, j) => (j === i ? true : v))), 80 + i * 120))
    return () => timers.forEach((t) => window.clearTimeout(t))
    // Grows once, on arrival; a new answer moves the widths by transition.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduced])

  return (
    <div className="hc-funnel">
      {steps.map((s, i) => {
        const width = (s.count / max) * 100 + '%'
        const rows = [{ label: s.label, value: String(s.count), color }]
        const inner = (
          <>
            <span>{s.label}</span>
            <span className="hc-fbar" aria-hidden="true">
              <i style={{ width: grown[i] ? width : 0, opacity: SHADES[i] ?? 0.5, background: color }} />
            </span>
            <b className="hc-num">
              <Counted value={s.count} />
            </b>
          </>
        )
        const events = {
          onPointerMove: (e: React.PointerEvent) => tip.show(e, title, rows),
          onPointerLeave: () => tip.hide(),
          onFocus: (e: React.FocusEvent) => tip.showAt(e.currentTarget.querySelector('.hc-fbar') ?? e.currentTarget, title, rows),
          onBlur: () => tip.hide(),
        }
        return s.href ? (
          <a key={s.key} className="hc-fstep" href={s.href} aria-label={s.label + ': ' + s.count} {...events}>
            {inner}
          </a>
        ) : (
          <div key={s.key} className="hc-fstep" {...events}>
            {inner}
          </div>
        )
      })}
    </div>
  )
}

function Counted({ value }: { value: number }) {
  return <>{formatIN(useCountUp(value, 700))}</>
}
